// src/components/BCP/ISO27001Modal.tsx - ISO 27001 Disaster Recovery Plan Modal
import React, { useState, useEffect } from 'react';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';
import SubscriptionUpgradeModal from '../SubscriptionUpgradeModal';
import { getAccessToken } from '../../utils/authToken';

interface ISO27001ModalProps {
  isOpen: boolean;
  onClose: () => void;
  bcpId: number;
  bcpName: string;
}

interface BCPData {
  id: number;
  business_name: string;
  business_type: string;
  industry: string;
  employee_count: string;
  annual_revenue: string;
  critical_functions: Array<{
    name: string;
    description: string;
    criticality: string;
    max_downtime: string;
    rto: string;
    rpo: string;
    responsible_team: string;
  }>;
  it_systems: Array<{
    name: string;
    criticality: string;
    backup_location: string;
    rto: string;
    rpo: string;
  }>;
  risks: Array<{
    risk_name: string;
    impact: string;
    likelihood: string;
    mitigation: string;
  }>;
  emergency_contacts: Array<{
    name: string;
    role: string;
    phone: string;
    email: string;
  }>;
  recovery_procedures: Array<{
    procedure_name: string;
    description: string;
    responsible_party: string;
    estimated_time: string;
  }>;
}

const ISO27001Modal: React.FC<ISO27001ModalProps> = ({
  isOpen,
  onClose,
  bcpId,
  bcpName
}) => {
  const { canAccessFeature } = useSubscriptionContext();
  const { user } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [bcpData, setBcpData] = useState<BCPData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canAccess = canAccessFeature('iso_27001_readiness') ||
    (user && (user.is_superuser || user.role === 'ADMIN' || user.role === 'SUPER_ADMIN'));

  useEffect(() => {
    if (isOpen && bcpId && canAccess) {
      loadBCPData();
    }
  }, [isOpen, bcpId, canAccess]);

  const loadBCPData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE_URL}/v1/bcp/${bcpId}/iso27001`, {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBcpData(data);
      } else if (response.status === 403) {
        setError('Premium subscription required for ISO 27001 Disaster Recovery Plan');
      } else {
        setError('Failed to load ISO 27001 document');
      }
    } catch (err) {
      setError('Error loading ISO 27001 document');
      console.error('Error loading ISO 27001 document:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Create a printable version of the content
    const printContent = document.getElementById('iso27001-content');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>ISO 27001 Disaster Recovery Plan - ${bcpName}</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                .header { text-align: center; margin-bottom: 30px; }
                .section { margin-bottom: 25px; }
                .section-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; color: #1f2937; }
                .subsection-title { font-size: 16px; font-weight: bold; margin-bottom: 8px; color: #374151; }
                .content { margin-bottom: 15px; line-height: 1.6; }
                .table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
                .table th, .table td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
                .table th { background-color: #f3f4f6; font-weight: bold; }
                .list { margin-left: 20px; }
                .highlight { background-color: #fef3c7; padding: 15px; border-left: 4px solid #f59e0b; margin: 15px 0; }
                @media print { .no-print { display: none; } }
              </style>
            </head>
            <body>
              ${printContent.innerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  if (!isOpen) return null;

  // Show upgrade modal if user doesn't have access
  if (!canAccess) {
    return (
      <SubscriptionUpgradeModal
        isOpen={isOpen}
        onClose={onClose}
        requiredFeature="premium"
        featureName="ISO 27001 Disaster Recovery Plan"
      />
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="relative w-full max-w-6xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
              <div className="flex items-center space-x-3">
                <div className="flex justify-center items-center w-10 h-10 bg-white bg-opacity-20 rounded-xl">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold">ISO 27001 Disaster Recovery Plan</h2>
                  <p className="text-sm opacity-90">{bcpName}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
                >
                  <svg className="inline mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download
                </button>
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
                >
                  <svg className="inline mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print
                </button>
                <button
                  onClick={onClose}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="text-red-600 mb-4">{error}</div>
                  <button
                    onClick={loadBCPData}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Retry
                  </button>
                </div>
              ) : bcpData ? (
                <div id="iso27001-content" className="space-y-8">
                  {/* Document Header */}
                  <div className="text-center border-b pb-6">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Disaster Recovery Plan (DRP)</h1>
                    <p className="text-lg text-gray-600 mb-4">Aligned with ISO/IEC 27001:2022</p>
                    <div className="text-sm text-gray-500">
                      <strong>{bcpData.business_name}</strong><br />
                      {bcpData.business_type} | {bcpData.industry}<br />
                      {bcpData.employee_count} Employees | {bcpData.annual_revenue} Annual Revenue<br />
                      Generated: {new Date().toLocaleDateString()}
                    </div>
                  </div>

                  {/* 1. Purpose and Scope */}
                  <div className="section">
                    <h2 className="section-title">1. Purpose and Scope</h2>
                    <div className="content">
                      This Disaster Recovery Plan (DRP) outlines the strategies and procedures for {bcpData.business_name}
                      to maintain critical business operations during and after disruptive incidents. The plan ensures
                      business resilience, protects stakeholder interests, and maintains service delivery capabilities
                      aligned with ISO/IEC 27001:2022 for information security management systems.
                    </div>
                    <div className="highlight">
                      <strong>Key Objectives:</strong>
                      <ul className="list">
                        <li>Minimize business disruption during incidents</li>
                        <li>Protect critical business functions and information assets</li>
                        <li>Ensure timely recovery of operations</li>
                        <li>Maintain stakeholder confidence and trust</li>
                        <li>Support alignment with ISO/IEC 27001:2022 information security requirements</li>
                      </ul>
                    </div>
                  </div>

                  {/* 2. Roles and Responsibilities */}
                  <div className="section">
                    <h2 className="section-title">2. Roles and Responsibilities</h2>
                    <div className="content">
                      The following roles and responsibilities are defined for disaster recovery operations:
                    </div>
                    {bcpData.emergency_contacts && bcpData.emergency_contacts.length > 0 && (
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Role</th>
                            <th>Name</th>
                            <th>Contact Information</th>
                            <th>Responsibilities</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bcpData.emergency_contacts.map((contact, index) => (
                            <tr key={index}>
                              <td>{contact.role}</td>
                              <td>{contact.name}</td>
                              <td>
                                {contact.phone}<br />
                                {contact.email}
                              </td>
                              <td>
                                {contact.role === 'Incident Commander' && 'Overall incident coordination and decision making'}
                                {contact.role === 'IT Director' && 'Technical recovery and system restoration'}
                                {contact.role === 'Communications Manager' && 'Internal and external communications'}
                                {contact.role === 'Business Continuity Manager' && 'Business process recovery coordination'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* 3. Risk and Impact Reference */}
                  <div className="section">
                    <h2 className="section-title">3. Risk and Impact Reference</h2>
                    <div className="content">
                      The following risks have been identified and assessed for their potential impact on business operations:
                    </div>
                    {bcpData.risks && bcpData.risks.length > 0 && (
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Risk</th>
                            <th>Impact Level</th>
                            <th>Likelihood</th>
                            <th>Mitigation Strategy</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bcpData.risks.map((risk, index) => (
                            <tr key={index}>
                              <td>{risk.risk_name}</td>
                              <td>{risk.impact}</td>
                              <td>{risk.likelihood}</td>
                              <td>{risk.mitigation}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* 4. Disaster Recovery Strategies */}
                  <div className="section">
                    <h2 className="section-title">4. Disaster Recovery Strategies (Generated by AI)</h2>
                    <div className="content">
                      Based on the business profile and risk assessment, the following disaster recovery strategies are recommended:
                    </div>
                    <div className="subsection-title">4.1 Information System Recovery</div>
                    <ul className="list">
                      <li>Implement automated backup systems with real-time replication</li>
                      <li>Establish redundant data centers with geographic separation</li>
                      <li>Deploy cloud-based disaster recovery solutions</li>
                      <li>Maintain offline backup copies of critical data</li>
                    </ul>
                    <div className="subsection-title">4.2 Business Process Recovery</div>
                    <ul className="list">
                      <li>Identify and prioritize critical business functions</li>
                      <li>Develop alternative work procedures for key processes</li>
                      <li>Establish remote work capabilities for essential personnel</li>
                      <li>Create vendor and supplier contingency plans</li>
                    </ul>
                  </div>

                  {/* 5. Incident Response and Escalation */}
                  <div className="section">
                    <h2 className="section-title">5. Incident Response and Escalation</h2>
                    <div className="content">
                      The following escalation procedures will be followed during a disaster:
                    </div>
                    <ol className="list">
                      <li><strong>Detection:</strong> Incident detection through monitoring systems or staff reports</li>
                      <li><strong>Assessment:</strong> Initial assessment of impact and severity</li>
                      <li><strong>Notification:</strong> Immediate notification of incident commander and key personnel</li>
                      <li><strong>Activation:</strong> Activation of disaster recovery procedures</li>
                      <li><strong>Response:</strong> Implementation of recovery strategies</li>
                      <li><strong>Recovery:</strong> Restoration of normal operations</li>
                      <li><strong>Review:</strong> Post-incident review and plan improvement</li>
                    </ol>
                  </div>

                  {/* 6. Recovery Procedures */}
                  <div className="section">
                    <h2 className="section-title">6. Recovery Procedures (Generated by AI)</h2>
                    <div className="content">
                      Detailed recovery procedures for critical systems and processes:
                    </div>
                    {bcpData.recovery_procedures && bcpData.recovery_procedures.length > 0 && (
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Procedure</th>
                            <th>Description</th>
                            <th>Responsible Party</th>
                            <th>Estimated Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bcpData.recovery_procedures.map((procedure, index) => (
                            <tr key={index}>
                              <td>{procedure.procedure_name}</td>
                              <td>{procedure.description}</td>
                              <td>{procedure.responsible_party}</td>
                              <td>{procedure.estimated_time}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* 7. Communication Plan */}
                  <div className="section">
                    <h2 className="section-title">7. Communication Plan</h2>
                    <div className="content">
                      Communication protocols for internal and external stakeholders during a disaster:
                    </div>
                    <div className="subsection-title">7.1 Internal Communications</div>
                    <ul className="list">
                      <li>Staff notification via multiple channels (email, SMS, phone)</li>
                      <li>Regular status updates during recovery operations</li>
                      <li>Clear instructions for remote work and alternative procedures</li>
                    </ul>
                    <div className="subsection-title">7.2 External Communications</div>
                    <ul className="list">
                      <li>Customer notification of service disruptions</li>
                      <li>Vendor and supplier coordination</li>
                      <li>Regulatory and compliance reporting as required</li>
                      <li>Media relations and public communications</li>
                    </ul>
                  </div>

                  {/* 8. Testing and Exercising */}
                  <div className="section">
                    <h2 className="section-title">8. Testing and Exercising</h2>
                    <div className="content">
                      Regular testing and exercising of disaster recovery procedures is essential for plan effectiveness:
                    </div>
                    <ul className="list">
                      <li><strong>Quarterly:</strong> Tabletop exercises and plan reviews</li>
                      <li><strong>Annually:</strong> Full-scale disaster recovery testing</li>
                      <li><strong>Ongoing:</strong> Backup system validation and monitoring</li>
                      <li><strong>Post-Incident:</strong> Plan updates based on lessons learned</li>
                    </ul>
                  </div>

                  {/* 9. Maintenance and Continuous Improvement */}
                  <div className="section">
                    <h2 className="section-title">9. Maintenance and Continuous Improvement</h2>
                    <div className="content">
                      This disaster recovery plan will be maintained and updated to ensure continued effectiveness:
                    </div>
                    <ul className="list">
                      <li>Regular review and update of contact information</li>
                      <li>Annual assessment of risks and mitigation strategies</li>
                      <li>Continuous improvement based on testing results</li>
                      <li>Integration with business continuity planning</li>
                      <li>Ongoing alignment with ISO/IEC 27001:2022 requirements</li>
                    </ul>
                  </div>

                  {/* Document Footer */}
                  <div className="border-t pt-6 text-center text-sm text-gray-500">
                    <p>This document is confidential and proprietary to {bcpData.business_name}</p>
                    <p>Document Version: 1.0 | Last Updated: {new Date().toLocaleDateString()}</p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ISO27001Modal;
